import { useEffect, useId, useState } from 'react';
import { cn } from '@playerbase/ui';

import { DetailsToggle } from './details-toggle.js';
import { FeedbackRow } from './feedback-row.js';
import { ForumPostCard } from './forum-post-card.js';
import { pollSourceLabel, pollVoteDetailFor } from './poll-activity.js';
import type { InboxGroup, InboxMessage } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// One feed entry. Branches on group.kind:
//   - 'forum-post' : Discord-style card. Title, author, preview,
//     reply summary; click to expand into the full conversation.
//   - 'thread' / 'reply-chain' : head row + optional collapsible
//     replies indented beneath. Default open if recent.
//   - 'single'     : just the head row, no chrome.
//
// Once the user toggles a group, the choice sticks for the session;
// reloading or switching channels resets the state.
// ---------------------------------------------------------------------------

interface FeedbackGroupProps {
  group: InboxGroup;
  byExternalId: Map<string, InboxMessage>;
  // When set to this group's id, the activity-panel click flow has
  // requested we be expanded. Local toggle state is still respected
  // afterwards - the override only flips us open, it does not lock.
  forceOpenGroupId?: string | null | undefined;
  // The InboxRow id that should pulse with the activity highlight.
  // Forwarded straight through to FeedbackRow / ForumPostCard so each
  // row can match against its own id.
  highlightRowId?: string | null | undefined;
  // Per-row ref registrar for scrollIntoView after expansion. Only
  // the row whose id matches gets registered; others are skipped.
  registerRowRef?:
    | ((rowId: string, el: HTMLDivElement | null) => void)
    | undefined;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function isWithin24h(timestamp: string): boolean {
  const t = new Date(timestamp).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < ONE_DAY_MS;
}

function relativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();
  if (diff < 0) return 'just now';
  if (diff < 60_000) return 'just now';
  if (diff < ONE_DAY_MS) {
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  }
  const days = Math.floor(diff / ONE_DAY_MS);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function lastReplyTimestamp(group: InboxGroup): string | null {
  // Latest message-level activity across replies. Reactions don't
  // count for this summary - the line says "last reply", not "last
  // activity" - reaction highlights live on the pills already.
  let latest: string | null = null;
  for (const r of group.replies) {
    for (const m of r.messages) {
      if (m.postedAt === null) continue;
      if (latest === null || m.postedAt > latest) latest = m.postedAt;
    }
  }
  return latest;
}

function summaryFor(group: InboxGroup): string {
  const n = group.replies.length;
  const noun = n === 1 ? 'reply' : 'replies';
  const last = lastReplyTimestamp(group);
  if (last === null) return `${n} ${noun}`;
  return `${n} ${noun} · last ${relativeTime(last)}`;
}

export function FeedbackGroup({
  group,
  byExternalId,
  forceOpenGroupId = null,
  highlightRowId = null,
  registerRowRef,
}: FeedbackGroupProps): JSX.Element {
  const disclosureId = useId();
  const hasReplies = group.replies.length > 0;
  // Forum posts default to collapsed (the card is the resting state,
  // matching Discord's forum-channel browse). Inline thread / reply
  // chains default open if the head was active within 24h.
  const [open, setOpen] = useState<boolean>(() => {
    if (group.kind === 'forum-post') return false;
    return hasReplies ? isWithin24h(group.head.lastActivityAt) : false;
  });

  // Force-expand when this group is the click target. We never
  // force-collapse - if the user already had it open, leave it open;
  // the override is one-way "open me now". A re-click of the same
  // activity item should still re-fire scroll, which is why the inbox
  // container intentionally re-sets a same-value forceOpenGroupId.
  useEffect(() => {
    if (forceOpenGroupId === group.id && !open) setOpen(true);
  }, [forceOpenGroupId, group.id, open]);

  const headRef =
    registerRowRef !== undefined
      ? (el: HTMLDivElement | null) => registerRowRef(group.head.id, el)
      : undefined;

  if (group.kind === 'forum-post') {
    return (
      <ForumPostCard
        group={group}
        byExternalId={byExternalId}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        controlsId={disclosureId}
        highlightRowId={highlightRowId}
        registerRowRef={registerRowRef}
      />
    );
  }

  // Poll-related rows in the message feed get the same banner +
  // vote-detail treatment as activity-panel entries: a small source
  // label above ("voted in #channel" / "created poll" / "poll
  // closed"), and the voted answer list under the poll pill. Mirrors
  // the activity panel so a vote landing in either place reads the
  // same way.
  const pollLabel = pollSourceLabel(group);
  const voteDetail = pollVoteDetailFor(group);

  return (
    <div>
      {pollLabel !== null && (
        <div className="px-4 pt-2 text-xs text-text-muted">{pollLabel}</div>
      )}
      <FeedbackRow
        row={group.head}
        byExternalId={byExternalId}
        highlighted={highlightRowId === group.head.id}
        innerRef={headRef}
        {...(voteDetail !== null ? { pollVoteDetail: voteDetail } : {})}
      />
      {hasReplies && (
        <div className="px-4 pb-3 -mt-1">
          <DetailsToggle
            open={open}
            onToggle={setOpen}
            summary={summaryFor(group)}
            controlsId={disclosureId}
          />
          {/* CSS grid-template-rows trick: animating between 0fr and
              1fr lets the wrapper shrink to / grow to its content's
              natural height without hard-coding a max-height. The
              inner overflow-hidden hides content during the collapse.
              200ms ease-out keeps it snappy. Content stays in the DOM
              when closed so accessibility (aria-controls target) and
              the grow direction both work. */}
          <div
            id={disclosureId}
            className={cn(
              'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:duration-0',
              open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
            aria-hidden={!open}
          >
            <div className="overflow-hidden">
              <div className="mt-1 ml-2 border-l-2 border-border pl-2 divide-y divide-border">
                {group.replies.map((r) => (
                  <FeedbackRow
                    key={r.id}
                    row={r}
                    byExternalId={byExternalId}
                    highlighted={highlightRowId === r.id}
                    innerRef={
                      registerRowRef !== undefined
                        ? (el) => registerRowRef(r.id, el)
                        : undefined
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
